import { InfiniteData, useQueryClient } from '@tanstack/react-query';

import {
  BestDoctorReviewQuery,
  GetDoctorReviewQuery,
  GetDoctorReviewQueryVariables,
  GetHospitalByIdQuery,
  LikeReviewMutationVariables,
} from '@/types/bff/gql-schema';

import useGetHospitalById from '@/hooks/hospital/useGetHospitalById';
import { useBestDoctorReviewQuery, useLikeReviewMutation } from './doctor.gql.impl';
import useGetDoctorReviewInfinityQuery from './useGetDoctorReviewInfinityQuery';
import { GetDoctorReviewsParams, getDoctorReviewSort } from './useGetDoctorReviews';

const useLikeReview = (params: GetDoctorReviewsParams & { hospitalId?: string }) => {
  const queryClient = useQueryClient();

  const doctorReviewRequest = {
    doctorReviewRequest: {
      doctorPublicId: params.doctorId,
      diagnosisTypeCriteria: params.diagnosisTypeCriteria,
    },
  };

  const doctorReviewsKey = {
    ...doctorReviewRequest,
    doctorReviewSort: getDoctorReviewSort(params.sort),
    pagingRequest: {
      page: 1,
      limit: 10,
    },
  };

  /**
   * Normal Review like
   */

  const onMutateNormalReviewLikeByPid = (mutateParams: LikeReviewMutationVariables) => {
    const reviewsQueryKey = useGetDoctorReviewInfinityQuery.getKey(doctorReviewsKey as GetDoctorReviewQueryVariables);
    const previousReviews: InfiniteData<GetDoctorReviewQuery> | undefined = queryClient.getQueryData(reviewsQueryKey);

    queryClient.setQueryData(reviewsQueryKey, {
      pages: previousReviews?.pages?.map((page) => {
        return {
          doctorReview: page.doctorReview.map((review) => {
            if (review.reviewId === mutateParams.reviewId) {
              return {
                ...review,
                isLiked: true,
                likeCount: review.likeCount + 1,
              };
            }
            return review;
          }),
        };
      }),
    });
  };

  // Normal Review Like Mutate
  const { mutate: mutateNormalReviewLike } = useLikeReviewMutation({
    onMutate: onMutateNormalReviewLikeByPid,
  });

  /**
   * Best Review like
   */

  // Best Review Like onMutate by doctorPid
  const onMutateBestReviewLikeByPid = () => {
    const bestDoctorReviewQueryKey = useBestDoctorReviewQuery.getKey({
      doctorPublicId: params.doctorId,
    });
    const previousReviews: BestDoctorReviewQuery | undefined = queryClient.getQueryData(bestDoctorReviewQueryKey);

    queryClient.setQueryData(bestDoctorReviewQueryKey, {
      bestDoctorReview: {
        ...previousReviews?.bestDoctorReview,
        isLiked: true,
        likeCount: previousReviews?.bestDoctorReview?.likeCount ? previousReviews.bestDoctorReview.likeCount + 1 : 1,
      },
    });
  };

  // Best Review Like Mutate
  const { mutate: mutateBestReviewLike } = useLikeReviewMutation({
    onMutate: onMutateBestReviewLikeByPid,
  });

  const onMutateHospitalTopRatedReviewLike = (mutateParams: LikeReviewMutationVariables) => {
    if (!params.hospitalId) {
      console.warn('Hospital ID not provided for top rated review like');
      return;
    }

    const hospitalQueryKey = useGetHospitalById.getKey(params.hospitalId);
    const previousHospitalData = queryClient.getQueryData<GetHospitalByIdQuery>(hospitalQueryKey);

    if (!previousHospitalData) {
      console.warn('No hospital data found in cache');
      return;
    }

    const updatedTopRatedReviews = previousHospitalData.getHospitalByPublicId.topRatedDoctorReviews.map((review) => {
      if (review.id === mutateParams.reviewId) {
        return {
          ...review,
          isLiked: true,
          likeCount: review.likeCount + 1,
        };
      }
      return review;
    });

    const updatedHospitalData: GetHospitalByIdQuery = {
      ...previousHospitalData,
      getHospitalByPublicId: {
        ...previousHospitalData.getHospitalByPublicId,
        topRatedDoctorReviews: updatedTopRatedReviews,
      },
    };

    queryClient.setQueryData(hospitalQueryKey, updatedHospitalData);
  };

  const { mutate: mutateHospitalTopRatedReviewLike } = useLikeReviewMutation({
    onMutate: onMutateHospitalTopRatedReviewLike,
  });

  const bestReviewLikeMutate = (reviewId: string) => {
    return mutateBestReviewLike({
      reviewId,
    });
  };

  const normalReviewLikeMutate = (reviewId: string) => {
    return mutateNormalReviewLike({
      reviewId,
    });
  };

  const hospitalTopRatedReviewLikeMutate = (reviewId: string) => {
    return mutateHospitalTopRatedReviewLike({
      reviewId,
    });
  };

  return {
    bestReviewLikeMutate,
    normalReviewLikeMutate,
    hospitalTopRatedReviewLikeMutate,
  };
};

export default useLikeReview;
